import {
  recordEstateIfRecording,
  recordEstatesOnAddressIfRecording,
  recordOwnedEstateIfRecording,
  recordOwnedEstatesIfRecording,
} from "Utils/fixtureRecordingUtils";
import { createRequest, getApiError } from "Utils/requestUtils";
import {
  CallEffect,
  SelectEffect,
  call,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import {
  Cadastre,
  EstateErrorCode,
  EstateIdentity,
  EstateInformation,
  OperationType,
} from "./estateInterfaces";
import {
  fetchEstateOnAddressStart,
  fetchEstateOnAddressSuccess,
  fetchOwnedEstateStart,
  fetchOwnedEstateSuccess,
  fetchOwnedEstatesStart,
  fetchhOwnedEstatesSuccess,
  operationFailed,
} from "./estateSlice";

import { HouseType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseTypes";
import { customerRefSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { isDefined } from "Utils/functions";
import { AxiosResponse } from "axios";
import { isValidHouseEstate } from "./estateUtils";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchEstateOnAddress({
  payload: { addressId, recentlyBought, onSuccess },
}: ReturnType<typeof fetchEstateOnAddressStart>) {
  try {
    const estateIdentities = (yield call(
      fetchEstateIdentitiesOnAddressActual,
      addressId,
    )) as EstateIdentity[];
    recordEstatesOnAddressIfRecording(estateIdentities);
    const estateIdentity = estateIdentities.find(isValidHouseEstate);
    if (isDefined(estateIdentity)) {
      yield call(
        fetchEstateInformation,
        estateIdentity.cadastre,
        recentlyBought,
        onSuccess,
      );
    } else {
      yield put(noValidHouseError(OperationType.GET_ESTATE));
    }
  } catch (e) {
    yield put(operationFailed(getApiError(e, OperationType.GET_ESTATE)));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchEstateInformation(
  cadastre: Cadastre,
  recentlyBought?: boolean,
  onSuccess?: (estateInformation: EstateInformation) => void,
) {
  const estateInformation = (yield isAuthenticated()
    ? call(fetchSecuredEstateInformationByCadastre, cadastre, recentlyBought)
    : call(
        fetchOpenEstateInformationByCadastre,
        cadastre,
      )) as EstateInformation;
  recordEstateIfRecording(estateInformation);
  if (
    estateInformation.houseType === HouseType.OTHER ||
    estateInformation.houseType === HouseType.AGRICULTURAL
  ) {
    yield put(noValidHouseError(OperationType.GET_ESTATE));
  } else if (isDefined(estateInformation.customerErrors)) {
    yield put(estateHasUWErrors(estateInformation, OperationType.GET_ESTATE));
  } else {
    yield put(fetchEstateOnAddressSuccess(estateInformation));
    onSuccess?.(estateInformation);
  }
}

function* fetchOpenEstateInformationByCadastre({
  kNr,
  gNr,
  bNr,
  fNr,
  sNr,
}: Cadastre): Generator<
  CallEffect<AxiosResponse<unknown>> | SelectEffect,
  EstateInformation,
  AxiosResponse<EstateInformation> | string | undefined
> {
  const customerRef = (yield select(customerRefSelector)) as string | undefined;
  const { data: estateInformation } = (yield call(createRequest, {
    method: "post",
    url: `/open/insurance/commerce/estate/cadastre/${kNr}/${gNr}/${bNr}/${fNr}/${sNr}`,
    requestBody: { customerRef },
  })) as AxiosResponse<EstateInformation>;
  return estateInformation;
}

function* fetchEstateIdentitiesOnAddressActual(
  addressId: number,
): Generator<
  CallEffect<AxiosResponse<unknown>>,
  EstateIdentity[],
  AxiosResponse<EstateIdentity[]>
> {
  const { data: estateIdentities } = yield call(createRequest, {
    url: `/open/insurance/estate/id/${addressId}`,
  });
  return estateIdentities;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchOwnedEstates() {
  try {
    const estates = (yield call(fetchOwnedEstatesActual)) as EstateIdentity[];
    recordOwnedEstatesIfRecording(estates);
    yield put(fetchhOwnedEstatesSuccess(estates.filter(isValidHouseEstate)));
  } catch (e) {
    yield put(operationFailed(getApiError(e, OperationType.GET_OWNED_ESTATES)));
  }
}

function* fetchOwnedEstatesActual(): Generator<
  CallEffect<AxiosResponse<unknown>>,
  EstateIdentity[],
  AxiosResponse<EstateIdentity[]>
> {
  const { data: estates } = yield call(createRequest, {
    url: "/insurance/estate/owned",
    open: false,
  });
  return estates;
}

function estateHasUWErrors(
  estateInformation: EstateInformation,
  operationType: OperationType,
): ReturnType<typeof operationFailed> {
  return operationFailed({
    status: 400,
    response: {
      errors:
        estateInformation.customerErrors?.map((uwCode) => ({
          errorCode: uwCode,
        })) ?? [],
    },
    operationType,
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchOwnedEstateInformation({
  payload: { cadastre, onSuccess },
}: ReturnType<typeof fetchOwnedEstateStart>) {
  try {
    const estateInformation = (yield call(
      fetchSecuredEstateInformationByCadastre,
      cadastre,
    )) as EstateInformation;
    recordOwnedEstateIfRecording(estateInformation);
    if (
      estateInformation.houseType === HouseType.OTHER ||
      estateInformation.houseType === HouseType.AGRICULTURAL
    ) {
      yield put(noValidHouseError(OperationType.GET_OWNED_ESTATE));
    } else if (isDefined(estateInformation.customerErrors)) {
      yield put(
        estateHasUWErrors(estateInformation, OperationType.GET_OWNED_ESTATE),
      );
    } else {
      yield put(fetchOwnedEstateSuccess(estateInformation));
      onSuccess?.(estateInformation);
    }
  } catch (e) {
    yield put(operationFailed(getApiError(e, OperationType.GET_OWNED_ESTATE)));
  }
}

function* fetchSecuredEstateInformationByCadastre(
  { kNr, gNr, bNr, fNr, sNr }: Cadastre,
  recentlyBought?: boolean,
): Generator<
  CallEffect<AxiosResponse<unknown>> | SelectEffect,
  EstateInformation,
  AxiosResponse<EstateInformation> | string | undefined
> {
  const { data: estateInformation } = (yield call(createRequest, {
    url: `/insurance/commerce/estate/cadastre/${kNr}/${gNr}/${bNr}/${fNr}/${sNr}`,
    params: { newlyPurchased: String(recentlyBought) },
    open: false,
  })) as AxiosResponse<EstateInformation>;
  return estateInformation;
}

function noValidHouseError(
  operation: OperationType,
): ReturnType<typeof operationFailed> {
  return operationFailed({
    status: 404,
    response: { errors: [{ errorCode: EstateErrorCode.NO_VALID_HOUSE }] },
    operationType: operation,
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* estateSaga() {
  yield takeLatest(fetchEstateOnAddressStart, fetchEstateOnAddress);
  yield takeLatest(fetchOwnedEstatesStart, fetchOwnedEstates);
  yield takeLatest(fetchOwnedEstateStart, fetchOwnedEstateInformation);
}
